// A small glossary of grammar terms that show up in exercise prompts.
// Each entry powers an inline hover tooltip in <GlossaryText> so students who
// don't know a term can get a one-line explanation plus an example or two.
//
// Keys are matched case-insensitively, on word boundaries, with an optional
// trailing "s" for plurals (so "prepositions" matches "preposition").
// `aliases` lets several phrasings point at the same entry.

export interface GlossaryEntry {
  /** Heading shown at the top of the tooltip. */
  term: string;
  /** One short sentence — what the term means. */
  definition: string;
  /** One or two illustrative Italian examples. */
  examples: string[];
  /** Extra phrasings that should trigger the same tooltip. */
  aliases?: string[];
}

export const GRAMMAR_GLOSSARY: GlossaryEntry[] = [
  {
    term: "preposition",
    definition:
      "A small linking word that shows relationship — place, time, possession or direction.",
    examples: ["di, a, da, in, con, su, per", "Vado a Roma. · La casa di Marco."],
    aliases: ["preposizione"],
  },
  {
    term: "articulated preposition",
    definition:
      "A preposition fused with a definite article into one word.",
    examples: ["a + il → al", "di + la → della · in + i → nei"],
    aliases: [
      "prepositional article",
      "preposizione articolata",
      "preposizioni articolate",
    ],
  },
  {
    term: "temporal expression",
    definition:
      "A word or phrase that locates an action in time — when, how long, how often.",
    examples: ["ieri, da un po', fa, mentre", "L'ho visto un'ora fa."],
    aliases: [
      "time expression",
      "expression of time",
      "espressione temporale",
      "espressioni temporali",
      "espressione di tempo",
    ],
  },
  {
    term: "definite article",
    definition: "The word for “the”, agreeing in gender and number with the noun.",
    examples: ["il, lo, la, i, gli, le", "il libro · la casa · gli amici"],
    aliases: ["articolo determinativo", "articoli determinativi"],
  },
  {
    term: "indefinite article",
    definition: "The word for “a / an”, agreeing with the noun's gender.",
    examples: ["un, uno, una, un'", "un cane · una mela"],
    aliases: ["articolo indeterminativo", "articoli indeterminativi"],
  },
  {
    term: "article",
    definition: "A short word before a noun marking it as definite (the) or indefinite (a).",
    examples: ["il, la, un, una", "il gatto · una sedia"],
    aliases: ["articolo"],
  },
  {
    term: "conjugation",
    definition: "The way a verb changes its ending to match the subject and tense.",
    examples: ["parlo, parli, parla", "io sono · tu sei · lui è"],
    aliases: ["conjugate", "coniugazione"],
  },
  {
    term: "auxiliary verb",
    definition: "A “helper” verb (essere or avere) used to build compound tenses.",
    examples: ["ho mangiato · sono andato", "avere, essere"],
    aliases: ["auxiliary", "verbo ausiliare", "ausiliare"],
  },
  {
    term: "past participle",
    definition: "The verb form used with an auxiliary to form past tenses.",
    examples: ["mangiato, finito, andato", "Ho parlato. · È partita."],
    aliases: ["participio passato"],
  },
  {
    term: "gerund",
    definition: "The -ando / -endo form, like English “-ing”, often after stare.",
    examples: ["parlando, leggendo", "Sto mangiando."],
    aliases: ["gerundio"],
  },
  {
    term: "infinitive",
    definition: "The base, unconjugated form of a verb — the dictionary entry.",
    examples: ["parlare, vedere, dormire", "Voglio mangiare."],
    aliases: ["infinito"],
  },
  {
    term: "subjunctive",
    definition: "A mood for doubt, wish, emotion or opinion, often after “che”.",
    examples: ["che io sia, che tu parli", "Penso che sia vero."],
    aliases: ["congiuntivo"],
  },
  {
    term: "conditional",
    definition: "The “would” mood — for hypotheses, requests and politeness.",
    examples: ["parlerei, vorrei", "Vorrei un caffè."],
    aliases: ["condizionale"],
  },
  {
    term: "imperfect",
    definition: "A past tense for ongoing, habitual or background actions.",
    examples: ["parlavo, ero, andava", "Da bambino giocavo sempre."],
    aliases: ["imperfetto"],
  },
  {
    term: "present perfect",
    definition: "The everyday past tense: auxiliary + past participle.",
    examples: ["ho mangiato, sono andato", "Ieri ho visto un film."],
    aliases: ["passato prossimo"],
  },
  {
    term: "reflexive verb",
    definition: "A verb whose action falls back on the subject, used with mi/ti/si…",
    examples: ["alzarsi, lavarsi", "Mi alzo alle sette."],
    aliases: ["reflexive", "verbo riflessivo", "riflessivo"],
  },
  {
    term: "agreement",
    definition: "Matching gender and number between words (e.g. noun and adjective).",
    examples: ["la casa rossa · i libri rossi", "le ragazze alte"],
    aliases: ["agree", "accordo", "concordanza"],
  },
  {
    term: "gender",
    definition: "Whether a noun is masculine or feminine — it shapes the words around it.",
    examples: ["il tavolo (m) · la sedia (f)", "un amico · un'amica"],
    aliases: ["genere"],
  },
  {
    term: "direct object pronoun",
    definition: "A pronoun replacing the thing directly receiving the action.",
    examples: ["mi, ti, lo, la, ci, vi, li, le", "Lo vedo. (I see him/it)"],
    aliases: ["pronome diretto", "pronomi diretti"],
  },
  {
    term: "indirect object pronoun",
    definition: "A pronoun for the person to/for whom something is done.",
    examples: ["mi, ti, gli, le, ci, vi", "Le parlo. (I speak to her)"],
    aliases: ["pronome indiretto", "pronomi indiretti"],
  },
  {
    term: "pronoun",
    definition: "A word that stands in for a noun.",
    examples: ["io, lo, questo, che", "Lo conosco."],
    aliases: ["pronome"],
  },
  {
    term: "adjective",
    definition: "A word that describes a noun, agreeing in gender and number.",
    examples: ["bello, grande, rosso", "una macchina veloce"],
    aliases: ["aggettivo"],
  },
  {
    term: "adverb",
    definition: "A word that modifies a verb, adjective or another adverb.",
    examples: ["bene, molto, lentamente", "Parla lentamente."],
    aliases: ["avverbio"],
  },
  {
    term: "subordinate clause",
    definition: "A clause that depends on a main clause, often introduced by che, quando, perché.",
    examples: ["…che è arrivato.", "Quando piove, resto a casa."],
    aliases: [
      "subordinate",
      "proposizione subordinata",
      "frase subordinata",
    ],
  },
  {
    term: "noun",
    definition: "A word for a person, place, thing or idea.",
    examples: ["casa, amico, libertà", "il libro · una città"],
    aliases: ["sostantivo"],
  },
  {
    term: "plural",
    definition: "The form used for more than one — usually a changed final vowel.",
    examples: ["libro → libri", "casa → case · amico → amici"],
    aliases: ["plurale"],
  },
  {
    term: "elision",
    definition: "Dropping a final vowel before another vowel, marked with an apostrophe.",
    examples: ["lo amico → l'amico", "una amica → un'amica"],
    aliases: ["elisione"],
  },
  {
    term: "accent",
    definition: "A mark on a final vowel showing stress, and sometimes meaning.",
    examples: ["è (is) vs e (and)", "città, perché, caffè"],
    aliases: ["accento"],
  },
];
